import { ManagementSummary, SplChip } from '../components/index.js';
import { LegalFooter } from '../components/LegalFooter.js';
import { ADVISORY, fallbackSrc, srcSet } from '../assets/images.js';
import { CATEGORIES, OFFERING, QUESTIONS } from '@shared/config.js';
import { DEPTH_LEVELS, INSTANT_SCAN_DEPTH, splDef } from '@shared/ndss-model.js';
import { REPORT_SECTIONS } from '@shared/report-sections.js';
import type { ReportSection } from '@shared/report-sections.js';
import type { CategoryResult, ScanResult } from '@shared/types.js';

/**
 * Screen 5 — the NDSS report.
 *
 * This is the FULL NDSS Scorecard layout, not an approximation of it: navy hero
 * with the score card and the category-posture bars on an SPL-0..SPL-4 scale,
 * then the document sections in their real order.
 *
 * Every widget sits at its real position. Where the Instant Scan has no data,
 * the widget's frame is still rendered and carries an explicit "no data at this
 * depth · available from Lx" state. Nothing is blurred or teased — the reader
 * sees precisely which parts of a real NDSS report a self-declared scan leaves
 * empty, which is the whole argument for a deeper scan.
 *
 * MVP sends no email: the Scorecard is an on-screen download.
 */

const section = (id: string): ReportSection => REPORT_SECTIONS.find((s) => s.id === id)!;

/** A widget frame with no data behind it at this depth. */
function EmptyWidget({ id }: { id: string }) {
  const s = section(id);
  return (
    <div className="rempty">
      <div className="rempty-top">
        <span className="rempty-badge">No data · from {s.unlockedAt}</span>
        <span className="rempty-title">{s.title}</span>
      </div>
      <p className="rempty-why">{s.why}</p>
      <p className="rempty-adds">
        <b>From {s.unlockedAt}:</b> {s.deepScanAdds}
      </p>
    </div>
  );
}

/** One row of the navy category-posture table. */
function PostureRow({ c }: { c: CategoryResult }) {
  // The bar runs on the 0–4 SPL scale, matching the SPL-0..SPL-4 columns.
  const pct = (c.mean / 4) * 100;
  return (
    <>
      <div className="rposture-name">{c.name}</div>
      <div className="rposture-track">
        <div className="rposture-fill" style={{ width: `${pct}%` }} />
        <i />
        <i />
        <i />
        <i />
        <i />
      </div>
      <div className="rposture-val">{c.mean.toFixed(1)}</div>
    </>
  );
}

export default function Results({
  result,
  scorecardUrl,
}: {
  result: ScanResult;
  scorecardUrl: string | null;
}) {
  const spl = splDef(result.spl);
  const sorted = [...result.categories].sort((a, b) => a.score - b.score);
  const exposures = sorted.slice(0, 3);
  const catalogueTotal = CATEGORIES.reduce((n, c) => n + c.catalogueTotal, 0);

  return (
    <div className="report">
      {/* ================= top bar ================= */}
      <header className="rtopbar">
        <img src="/assets/logo-white.svg" alt="NTT DATA" />
        <span className="rtopbar-sep" />
        <span className="rtopbar-title">{OFFERING.name}</span>
        <span className="rtopbar-crumb">· {OFFERING.tier} · self-declared</span>
        <span className="rtopbar-spacer" />
        <span className="rtopbar-status">Indicative · evidence E1</span>
        {scorecardUrl ? (
          <a className="rtopbar-btn" href={scorecardUrl} download target="_blank" rel="noreferrer">
            Download Scorecard
          </a>
        ) : (
          <span className="rtopbar-btn">Preparing…</span>
        )}
        <a
          className="rtopbar-btn rtopbar-btn--primary"
          href={`mailto:${OFFERING.contactEmail}?subject=${encodeURIComponent(
            'Deep Scan intro — 30 minutes',
          )}`}
        >
          Book a Deep Scan →
        </a>
      </header>

      {/* ================= navy hero ================= */}
      <div className="rhero">
        <div className="rhero-grid">
          <div className="rscore">
            <img src="/assets/logo-white.svg" alt="" />
            <p className="rscore-eyebrow">SOVEREIGNTY SCORE</p>
            <p className="rscore-org">Your organisation</p>
            <div className="rscore-num">
              {result.overall}
              <span>/100</span>
            </div>
            <span className="rscore-spl">
              <i />
              {spl.id} · {spl.label}
            </span>

            <div className="rscore-depth">
              <div className="rscore-depth-label">ASSESSMENT DEPTH</div>
              <div className="rscore-depth-track">
                {DEPTH_LEVELS.map((d) => (
                  <i key={d.id} className={d.id === INSTANT_SCAN_DEPTH ? 'on' : undefined} />
                ))}
              </div>
              <div className="rscore-depth-note">
                L1 · self-declared · {QUESTIONS.length} of {catalogueTotal} questions
              </div>
            </div>
          </div>

          <div>
            <p className="rposture-label">CATEGORY POSTURE</p>
            <div className="rposture">
              <div />
              <div className="rposture-head">
                <span>SPL-0</span>
                <span>SPL-1</span>
                <span>SPL-2</span>
                <span>SPL-3</span>
                <span>SPL-4</span>
              </div>
              <div />
              {result.categories.map((c) => (
                <PostureRow key={c.categoryId} c={c} />
              ))}
            </div>
          </div>
        </div>

        <p className="rhero-note">
          Sovereignty here is <b>informed, reversible control over risk</b> — not autarky. This
          is an indicative scan: every answer is self-declared, so the whole result sits at
          evidence level <b>E1</b>, the lowest of four. No control has been inspected or
          corroborated.
        </p>
      </div>

      {/* ================= body ================= */}
      <div className="rbody">
        {/* ---- top exposures · action areas ---- */}
        <section>
          <div className="rgrid-2">
            <div className="rpanel">
              <p className="rsection-eyebrow">TOP EXPOSURES</p>
              <h2 className="rpanel-h">Where you're most exposed</h2>
              {exposures.map((c, i) => (
                <div className="rexp" key={c.categoryId}>
                  <span
                    className="rexp-dot"
                    style={{ background: ['#E42600', '#FF7A00', '#FFC400'][i] }}
                    aria-hidden="true"
                  />
                  <div>
                    <span className="rexp-name">{c.shortTitle}</span>{' '}
                    <span className="rexp-why">
                      — {Math.round(c.score)}/100, {splDef(c.spl).label.toLowerCase()}; rests on{' '}
                      {c.answered} of {c.catalogueTotal} catalogue questions.
                    </span>
                  </div>
                </div>
              ))}
              <p className="rsection-foot">
                Ranked by score. Which of these actually governs your headline needs the full
                question set — at this depth each category rests on one or two answers.
              </p>
            </div>

            <div className="rpanel">
              <p className="rsection-eyebrow">ACTION AREAS</p>
              <h2 className="rpanel-h">What to fix first</h2>
              <EmptyWidget id="remediation" />
            </div>
          </div>
        </section>

        {/* ---- inventory in scope ---- */}
        <section>
          <p className="rsection-eyebrow">INVENTORY IN SCOPE</p>
          <h2 className="rsection-title">{section('inventory').title}</h2>
          <p className="rsection-sub">
            A consultant-led scan names every provider and workload in scope, its criticality and
            how far it was assessed — so the headline is traceable to a concrete, status-tagged
            inventory.
          </p>
          <div className="rgrid-2">
            <div className="rpanel">
              <h3 className="rpanel-h">Providers</h3>
              <EmptyWidget id="inventory" />
            </div>
            <div className="rpanel">
              <h3 className="rpanel-h">Workloads</h3>
              <div className="rempty">
                <div className="rempty-top">
                  <span className="rempty-badge">No data · from L2</span>
                  <span className="rempty-title">Workloads in scope</span>
                </div>
                <p className="rempty-why">
                  The Instant Scan asks about "your most critical system" as a single pass, so no
                  workload is named, tagged by criticality, or given an evidence status.
                </p>
                <p className="rempty-adds">
                  <b>From L2:</b> each workload listed with Critical / Important / Low and the
                  evidence actually reached, so scores are attributable per system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ---- category roll-up ---- */}
        <section>
          <p className="rsection-eyebrow">CATEGORY ROLL-UP</p>
          <h2 className="rsection-title">{section('category-rollup').title}</h2>
          <p className="rsection-sub">
            Each category is the mean of its questions, unless a <b>critical question</b> scores
            lower — then that question caps it. At this depth every category is governed by the
            mean: no control is flagged critical in a self-declared scan, so no ceiling can bite.
          </p>

          <div className="rrollup">
            <div className="rrollup-head">
              <span>CATEGORY</span>
              <span>MEAN → EFFECTIVE SCORE (0–4)</span>
              <span>GOVERNED BY</span>
              <span style={{ textAlign: 'right' }}>EFFECTIVE SPL</span>
            </div>
            {result.categories.map((c) => (
              <div className="rrollup-row" key={c.categoryId}>
                <div className="rrollup-cat">
                  <b>
                    {c.code} · w{c.weight}
                  </b>
                  <span>{c.shortTitle}</span>
                </div>
                <div>
                  <div className="rrollup-bar">
                    <i style={{ width: `${(c.mean / 4) * 100}%` }} />
                  </div>
                  <div className="rrollup-mean">
                    mean {c.mean.toFixed(2)} · {c.answered} of {c.catalogueTotal} answered
                  </div>
                </div>
                <span className="rrollup-gov">Mean</span>
                <span className="rrollup-spl">
                  <SplChip level={c.spl} />
                </span>
              </div>
            ))}
          </div>
          <p className="rsection-foot">
            Effective SPL is derived from the category score. With one or two questions per
            category and a single respondent, these means are indicative rather than settled.
          </p>
        </section>

        {/* ---- how instances roll up · where a ceiling bites ---- */}
        <section>
          <div className="rgrid-2">
            <div className="rpanel">
              <p className="rsection-eyebrow">HOW INSTANCES ROLL UP</p>
              <h3 className="rpanel-h">Per-provider and per-workload answers</h3>
              <div className="rempty">
                <div className="rempty-top">
                  <span className="rempty-badge">No data · from L2</span>
                  <span className="rempty-title">Instance roll-up</span>
                </div>
                <p className="rempty-why">
                  Provider- and workload-level questions are asked once here, about your main
                  cloud, so there are no instances to roll up into a category.
                </p>
                <p className="rempty-adds">
                  <b>From L2:</b> each question answered per instance, with the worst case and the
                  mean shown side by side.
                </p>
              </div>
            </div>
            <div className="rpanel">
              <p className="rsection-eyebrow">BINDING CONSTRAINTS</p>
              <h3 className="rpanel-h">{section('binding-constraints').title}</h3>
              <EmptyWidget id="binding-constraints" />
            </div>
          </div>
        </section>

        {/* ---- management summary (generic, keyed to SPL) ---- */}
        <section>
          <p className="rsection-eyebrow">MANAGEMENT SUMMARY</p>
          <h2 className="rsection-title">{section('management-summary').title}</h2>
          <ManagementSummary level={result.spl} />
        </section>

        {/* ---- findings ---- */}
        <section>
          <p className="rsection-eyebrow">FINDINGS</p>
          <h2 className="rsection-title">{section('findings').title}</h2>
          <p className="rsection-sub">
            Question-independent observations — no score of their own, optionally linked to the
            questions they touch.
          </p>
          <EmptyWidget id="findings" />
        </section>

        {/* ---- method ---- */}
        <section>
          <p className="rsection-eyebrow">METHOD</p>
          <h2 className="rsection-title">How this score is built</h2>
          <div className="rpanel">
            <p style={{ fontSize: 12.5, lineHeight: 1.7, color: 'var(--fg)', margin: 0 }}>
              The Instant Scan asks <b>{QUESTIONS.length} of the {catalogueTotal}</b> questions in
              the NDSS catalogue — one or two per category, each a real catalogue control answered
              on the same <b>0–4 SPL scale</b> a consultant would use. A category score is the mean
              of the questions asked × 25, giving 0–100. The overall NDSS is the weighted mean
              across the {CATEGORIES.length} categories (
              {CATEGORIES.map((c) => c.weight).join('/')}), then mapped to an SPL level. Because
              every answer is self-declared, the whole scan sits at depth <b>L1</b> and evidence{' '}
              <b>E1 / C1</b>.
            </p>
          </div>
        </section>

        {/* ---- parked ---- */}
        <section className="rparked">
          <span className="rparked-tag">FUTURE · NOT IN CURRENT MODEL</span>
          <span className="rparked-lead">
            Parked mechanisms — shown for orientation, not scored today.
          </span>
          <div className="rparked-grid">
            <div>
              <p className="rparked-h">Risk-profile gates</p>
              <p className="rparked-p">
                Required SPL minimums per profile (Standard Enterprise / Regulated / Critical
                Infrastructure) are <b>parked</b>. The current model reports posture and depth; it
                does not gate pass/fail against a profile.
              </p>
            </div>
            <div>
              <p className="rparked-h">Portability as a meta-provider</p>
              <p className="rparked-p">
                A forward-looking idea: proven, drilled portability could <b>offset</b> provider
                exposure — treating "we can leave within N days" as a sovereignty asset in its own
                right. Not built; recorded as direction.
              </p>
            </div>
          </div>
        </section>

        {/* ---- closing CTA ----
            Until now the only route to a conversation was the small button in
            the top bar, which is scrolled far out of sight by the time anyone
            reaches the end of the report. This repeats the offer where the
            reader actually is. */}
        <section className="rcta">
          <img
            className="rcta-photo"
            src={fallbackSrc(ADVISORY)}
            srcSet={srcSet(ADVISORY)}
            sizes="(min-width: 900px) 40vw, 100vw"
            alt={ADVISORY.alt}
            aria-hidden="true"
            loading="lazy"
            decoding="async"
          />
          <div className="rcta-body">
            <p className="rcta-eyebrow">NEXT STEP</p>
            <h2 className="rcta-title">See what the other {catalogueTotal - QUESTIONS.length}{' '}
              questions say</h2>
            <p className="rcta-p">
              This scan sits at depth <b>L1</b> — your answers alone. A Deep Scan adds documents,
              interviews and technical checks across all {CATEGORIES.length} categories, which is
              what moves the evidence level and makes the score defensible to an auditor.
            </p>
            <a
              className="rcta-btn"
              href={`mailto:${OFFERING.contactEmail}?subject=${encodeURIComponent(
                'Deep Scan intro — 30 minutes',
              )}`}
            >
              Book a 30-minute intro
            </a>
            <p className="rcta-note">No preparation needed. No obligation.</p>
          </div>
        </section>
      </div>

      <LegalFooter />
    </div>
  );
}
